/** Longest edge of the uploaded image. Instrument screens are text — 1600px is ample. */
const MAX_EDGE = 1600;
const THUMB_EDGE = 320;
const QUALITY = 0.82;

async function loadBitmap(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    // imageOrientation honours EXIF rotation; without it phone photos land sideways.
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = url;
    });
    return img;
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

function drawTo(src: ImageBitmap | HTMLImageElement, maxEdge: number): HTMLCanvasElement {
  const w = 'width' in src ? src.width : 0;
  const h = 'height' in src ? src.height : 0;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale));
  canvas.height = Math.max(1, Math.round(h * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src as CanvasImageSource, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Image encoding failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export interface PreparedImage {
  full: Blob;
  thumb: Blob;
  hash: string;
  width: number;
  height: number;
  previewUrl: string;
}

/** Content hash of the compressed bytes, used to skip re-extracting the same shot. */
async function sha256(blob: Blob): Promise<string> {
  try {
    const buf = await blob.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Non-secure contexts have no crypto.subtle. Caching is an optimisation only.
    return '';
  }
}

export async function prepareImage(file: Blob): Promise<PreparedImage> {
  const bitmap = await loadBitmap(file);
  const fullCanvas = drawTo(bitmap, MAX_EDGE);
  const thumbCanvas = drawTo(bitmap, THUMB_EDGE);
  const [full, thumb] = await Promise.all([toBlob(fullCanvas, QUALITY), toBlob(thumbCanvas, 0.7)]);
  if ('close' in bitmap) bitmap.close();
  return {
    full,
    thumb,
    hash: await sha256(full),
    width: fullCanvas.width,
    height: fullCanvas.height,
    previewUrl: URL.createObjectURL(full),
  };
}
